/**
 * SKNOTIFICATION-GLOBAL-CR-001 — template governance types. Mirrors
 * sk-notification-api's /governance/* responses 1:1, same convention as
 * schema/notification.ts (Backend* = wire shape, hand-written, no codegen).
 */
import type { ChannelType } from './notification';

export type MessageClassCode = 'authentication' | 'transactional' | 'service' | 'promotional';
export const MESSAGE_CLASSES: MessageClassCode[] = ['authentication', 'transactional', 'service', 'promotional'];

export type ApprovalState = 'draft' | 'submitted' | 'approved' | 'rejected' | 'paused';
export const APPROVAL_STATES: ApprovalState[] = ['draft', 'submitted', 'approved', 'rejected', 'paused'];

export type SuppressionKind = 'opt_out' | 'hard_bounce' | 'complaint' | 'dnd_registry';
export const SUPPRESSION_KINDS: SuppressionKind[] = ['opt_out', 'hard_bounce', 'complaint', 'dnd_registry'];

export const GOVERNANCE_CHANNELS: ChannelType[] = ['email', 'whatsapp', 'sms', 'push'];

export interface BackendTemplate {
  templateId: string;
  platformOrgId: string;
  templateKey: string;
  displayName: string;
  messageClassCode: MessageClassCode;
  isSystem: boolean;
  createdAt: string;
  deactivatedAt: string | null;
}

export interface BackendTemplateVariant {
  templateVariantId: string;
  templateId: string;
  localeTag: string;
  channelKind: ChannelType;
  subject: string | null;
  body: string;
  placeholderKeys: string[];
  createdAt: string;
}

export interface BackendTemplateApproval {
  templateApprovalId: string;
  templateId: string;
  channelKind: ChannelType;
  regionCode: string;
  approvalState: ApprovalState;
  externalProvider: string | null;
  externalTemplateId: string | null;
  submittedAt: string | null;
  decidedAt: string | null;
  contentHash: string | null;
}

export interface BackendTemplatePermission {
  templateId: string;
  jurisdictionCode: string;
  channelKind: ChannelType;
  messageClassCode: MessageClassCode;
  isPermitted: boolean;
  reasonCode: string | null;
  setBy: string | null;
  setAt: string;
}

export interface BackendTemplateDetail {
  template: BackendTemplate;
  variants: BackendTemplateVariant[];
  approvals: BackendTemplateApproval[];
  permissions: BackendTemplatePermission[];
}

export interface BackendSuppressionEntry {
  suppressionId: string;
  platformOrgId: string;
  recipientHash: string;
  channelKind: ChannelType;
  messageClassCode: MessageClassCode | null;
  suppressionKind: SuppressionKind;
  suppressedAt: string;
}

export interface BackendSendDecision {
  sendDecisionId: string;
  platformOrgId: string;
  templateId: string | null;
  recipientHash: string;
  resolvedJurisdiction: string;
  resolvedChannel: ChannelType;
  resolvedLocale: string;
  messageClassCode: MessageClassCode;
  decision: 'allow' | 'refuse' | 'deferred';
  gate1Approval: boolean | null;
  gate2Permission: boolean | null;
  consentOk: boolean | null;
  dndOk: boolean | null;
  quietHoursOk: boolean | null;
  frequencyOk: boolean | null;
  refusalReason: string | null;
  decidedAt: string;
}

/** A recipient identity as the API takes it (hashed server-side, never plaintext here). */
export interface RecipientIdentity {
  phone?: string;
  email?: string;
}
